/**
 * The Virtual Office's client (ADR-005, ADR-107, §16.4, AC-031).
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THE OFFICE IS READ-ONLY. There is no code path from this module to a
 * mutation, and there must never be one.
 *
 * This client is constructed with only two operations — an atomic snapshot and
 * a cursored subscription — and it issues nothing but `GET`. It does not accept,
 * hold or wrap a `ConsoleDataSource`, so a mutation added to that interface
 * cannot reach the Office by inheritance or by someone reaching through a field.
 * It carries no idempotency key and no actor identity, which are required on
 * every mutation endpoint (§20.1); an accidental mutation would therefore be
 * rejected by the API as well as being absent here.
 *
 * §16.4 also requires that the Office see no secrets, no claim-signing keys and
 * no mutation tokens, and AC-031 requires a security test proving its service
 * account holds Query-API scope only. That test lives in the security suite; the
 * shape of this file is the client-side half of the same claim.
 * ────────────────────────────────────────────────────────────────────────────
 */

import type { OfficeReadClient, SceneStreamHandlers, Subscription } from './data-source.js';
import { subscribeToScene } from './sse.js';
import type { SceneSnapshot } from './types.js';

export interface OfficeClientOptions {
  readonly baseUrl: string;
  /** Supplies the last cursor the renderer actually painted (see `CursorTracker`). */
  readonly resumeCursor: () => string | null;
  /**
   * Loopback session token.
   *
   * The Office is read-only (ADR-005), but read-only is not the same as
   * unauthenticated: the Control API requires a token on every request, and
   * without one this stream is refused before it opens.
   */
  readonly sessionToken?: string | null | undefined;
  readonly firebaseToken?: (() => Promise<string | null>) | undefined;
  /**
   * The team whose scene this client watches — the switcher's live choice
   * (§multi-team). Read per request, so a re-subscription after a switch
   * opens on the right team without rebuilding the client.
   */
  readonly currentTeamId?: (() => string) | undefined;
}

/**
 * A read that carries the Office's own token and nothing else.
 *
 * The previous version attached no credential at all, with a comment saying the
 * Office must not borrow the operator's session. The intent was right and the
 * result was a 401 on every read, because the Control API authenticates
 * everything. The query-scoped token is the answer the design already had: it
 * identifies the Office and cannot mutate.
 */
async function readOnlyInit(options: Pick<OfficeClientOptions, 'sessionToken' | 'firebaseToken'>): Promise<RequestInit> {
  const firebaseToken = await options.firebaseToken?.() ?? null;
  return {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      ...(firebaseToken !== null
        ? { Authorization: `Bearer ${firebaseToken}` }
        : options.sessionToken ? { 'x-autonomous-team-session': options.sessionToken } : {}),
    },
    credentials: 'same-origin',
    cache: 'no-store',
  };
}

export function createOfficeReadClient(options: OfficeClientOptions): OfficeReadClient {
  return {
    kind: 'live',
    label: 'Office projection (read-only)',

    async sceneSnapshot(): Promise<SceneSnapshot> {
      const teamId = options.currentTeamId?.() ?? '';
      const team = teamId.length === 0 ? '' : `&teamId=${encodeURIComponent(teamId)}`;
      const response = await fetch(
        `${options.baseUrl}/v1/events/stream?snapshot=1&projection=office${team}`,
        await readOnlyInit(options),
      );
      if (!response.ok) {
        throw new Error(`The office projection could not be read: ${String(response.status)} ${response.statusText}`);
      }
      return (await response.json()) as SceneSnapshot;
    },

    subscribeScene(cursor: string | null, handlers: SceneStreamHandlers): Subscription {
      return subscribeToScene(
        {
          baseUrl: options.baseUrl,
          resumeCursor: options.resumeCursor,
          sessionToken: options.sessionToken,
          firebaseToken: options.firebaseToken,
          ...(options.currentTeamId === undefined ? {} : { currentTeamId: options.currentTeamId }),
        },
        cursor,
        handlers,
      );
    },
  };
}
